import { Link } from 'react-router-dom';
import { LANGUAGE_BY_SLUG, relatedLanguages } from '../seo/languages';

/**
 * /languages/<slug> — one marketing/SEO page per detectable language.
 * Content is generated from src/seo/languages.js; the head tags come from the
 * matching route in src/seo/routes.js (rendered by the caller via SeoHelmet).
 */
function LanguagePage({ slug }) {
  const lang = LANGUAGE_BY_SLUG[slug];
  if (!lang) return null;
  const related = relatedLanguages(lang);

  return (
    <div className="page-container language-page">
      <nav className="breadcrumbs" aria-label="Breadcrumb">
        <Link to="/">LangShazam</Link>
        <span aria-hidden> / </span>
        <Link to="/languages">Languages</Link>
        <span aria-hidden> / </span>
        <span>{lang.name}</span>
      </nav>

      <header className="language-hero">
        <p className="language-hero-native" lang={lang.code}>{lang.nativeName}</p>
        <h1>Detect &amp; Transcribe {lang.name} Speech from Audio</h1>
        <p className="language-hero-lede">
          Tap the mic, speak or play {lang.name} audio, and LangShazam names the language in seconds
          and keeps a live {lang.name} transcript as you go. State-of-the-art spoken language
          detection, free with an account.
        </p>
        <Link to="/" className="language-cta-btn">Detect {lang.name} now →</Link>
        <p className="language-hero-alt">
          Have a recording? <Link to="/#upload">Upload a {lang.name} audio or video file</Link> — the first
          10 minutes are analyzed.
        </p>
      </header>

      <section className="content-card language-facts">
        <dl>
          <div><dt>Native name</dt><dd lang={lang.code}>{lang.nativeName}</dd></div>
          <div><dt>Language family</dt><dd>{lang.family}</dd></div>
          <div><dt>Writing system</dt><dd>{lang.script}</dd></div>
          <div><dt>Native speakers</dt><dd>{lang.speakers}</dd></div>
          <div><dt>Where it is spoken</dt><dd>{lang.countries.join(', ')}</dd></div>
          <div><dt>Say hello</dt><dd lang={lang.code}>{lang.greeting}</dd></div>
        </dl>
      </section>

      <section className="language-section">
        <h2>About {lang.name}</h2>
        <p>{lang.blurb}</p>
      </section>

      <section className="language-section">
        <h2>How LangShazam detects {lang.name}</h2>
        <p>
          Your browser streams audio to a real-time multilingual speech model that transcribes what it
          hears and identifies the language natively from the sound, not from guessing at text
          afterwards. Every finalized phrase carries its own language tag and confidence, so
          {' '}{lang.name} is recognised within a phrase or two, and a switch into another language
          mid-conversation is picked up as it happens.
        </p>
        <ol className="language-steps">
          <li><strong>Sign in</strong> with Google or email so your sessions are saved.</li>
          <li><strong>Tap the microphone</strong> and speak {lang.name}, or hold your phone near a recording, podcast or video.</li>
          <li><strong>Read the result</strong>: the detected language appears within seconds and the {lang.name} transcript updates live in {lang.script} script.</li>
        </ol>
      </section>

      {lang.confusedWith.length > 0 && (
        <section className="language-section">
          <h2>{lang.name} vs. {lang.confusedWith.join(' vs. ')}</h2>
          <p>
            {lang.name} is most often mistaken for {lang.confusedWith.join(' or ')}. Text-based
            detectors struggle because the written forms overlap; LangShazam listens to the audio,
            which is where the differences in rhythm, vowels and intonation live, and separates them
            reliably.
          </p>
        </section>
      )}

      <section className="language-section">
        <h2>What you can do with {lang.name} detection</h2>
        <ul className="language-usecases">
          <li>Identify a voice note, voicemail or recording you suspect is in {lang.name}.</li>
          <li>Find out what language a podcast, video or radio stream is in.</li>
          <li>Get a quick {lang.name} transcript of a conversation, lecture or meeting.</li>
          <li>Confirm which language a bilingual speaker switched into mid-sentence.</li>
        </ul>
      </section>

      {related.length > 0 && (
        <section className="language-section">
          <h2>Related languages</h2>
          <div className="lang-chips language-related">
            {related.map((r) => (
              <Link key={r.slug} to={`/languages/${r.slug}`} className="lang-chip">{r.name}</Link>
            ))}
            <Link to="/languages" className="lang-chip lang-chip-more">All 32 languages →</Link>
          </div>
        </section>
      )}

      <section className="language-section language-final-cta">
        <h2>Try it with {lang.name} audio</h2>
        <p>Free, works in any browser on desktop or mobile, no app to install.</p>
        <Link to="/" className="language-cta-btn">Open LangShazam →</Link>
      </section>
    </div>
  );
}

export default LanguagePage;
